import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

const PATH_D = 'M68,184 C110,172 150,166 198,150 C248,132 292,108 338,84 C368,68 406,58 426,50';

export default function IlluRates() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const line       = svg.querySelector('#ri-line');
            const area       = svg.querySelector('#ri-area-path');
            const dot1       = svg.querySelector('#ri-dot1');
            const dot2       = svg.querySelector('#ri-dot2');
            const peak       = svg.querySelector('#ri-peak');
            const tooltip    = svg.querySelector('#ri-tooltip');
            const badge      = svg.querySelector('#ri-badge');
            const chips      = svg.querySelectorAll('.ri-chip');
            const pulse1     = svg.querySelector('#ri-pulse1');
            const pulse2     = svg.querySelector('#ri-pulse2');
            const travelDot  = svg.querySelector('#ri-travel-dot');

            const pathLen = line.getTotalLength();

            // ── initial states ──
            gsap.set(line,       { strokeDasharray: pathLen, strokeDashoffset: pathLen });
            gsap.set(area,       { opacity: 0 });
            gsap.set([dot1, dot2], { transformBox: 'fill-box', transformOrigin: '50% 50%', scale: 0 });
            gsap.set(peak,       { transformBox: 'fill-box', transformOrigin: '50% 50%', scale: 0 });
            gsap.set(tooltip,    { opacity: 0, y: -10 });
            gsap.set(badge,      { transformBox: 'fill-box', transformOrigin: '50% 50%', scale: 0 });
            gsap.set(chips,      { opacity: 0, y: 16 });
            gsap.set(travelDot,  { opacity: 0 });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl
                .to(line,    { strokeDashoffset: 0, duration: 1.6, ease: 'power2.inOut' })
                .to(area,    { opacity: 1, duration: 1.0, ease: 'power1.out' }, 0.3)
                .to(dot1,    { scale: 1, duration: 0.4, ease: 'back.out(2.5)' }, 0.72)
                .to(dot2,    { scale: 1, duration: 0.4, ease: 'back.out(2.5)' }, 1.05)
                .to(peak,    { scale: 1, duration: 0.5, ease: 'back.out(2.5)' }, 1.35)
                .to(badge,   { scale: 1, duration: 0.45, ease: 'back.out(2.2)' }, 1.42)
                .to(tooltip, { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }, 1.62)
                .to(chips,   { opacity: 1, y: 0, duration: 0.5, ease: 'expo.out', stagger: 0.12 }, 1.58);

            // ── loop animations ──
            function startLoops() {

                // 1. Traveling dot rides the chart line (MotionPath)
                function launchDot() {
                    gsap.timeline({ onComplete: () => gsap.delayedCall(1.8, launchDot) })
                        .set(travelDot, { opacity: 1 })
                        .to(travelDot, {
                            motionPath: { path: line, align: line, alignOrigin: [0.5, 0.5], autoRotate: false },
                            duration: 2.4, ease: 'power1.inOut',
                        })
                        .to(travelDot, { opacity: 0, duration: 0.2, ease: 'power1.in' }, '-=0.25');
                }
                launchDot();

                // 2. Double concentric peak pulse (staggered)
                gsap.fromTo(pulse1,
                    { attr: { r: 6 }, opacity: 0.55 },
                    { attr: { r: 18 }, opacity: 0, duration: 1.5, ease: 'sine.out', repeat: -1, repeatDelay: 1.0 }
                );
                gsap.fromTo(pulse2,
                    { attr: { r: 6 }, opacity: 0.35 },
                    { attr: { r: 24 }, opacity: 0, duration: 1.8, ease: 'sine.out', repeat: -1, repeatDelay: 1.0, delay: 0.55 }
                );

                // 4. Area fill breathes
                gsap.to(area, {
                    opacity: 0.65, duration: 2.5, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // 5. Mid-point dots float
                gsap.to(dot1, { y: -3, duration: 1.8, ease: 'sine.inOut', yoyo: true, repeat: -1 });
                gsap.to(dot2, { y: -3, duration: 2.1, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.4 });

                // 6. Badge gentle pulse
                gsap.to(badge, {
                    scale: 1.05, transformBox: 'fill-box', transformOrigin: '50% 50%',
                    duration: 2.0, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // 7. Tooltip + chips slow float
                gsap.to(tooltip, { y: -2.5, duration: 2.2, ease: 'sine.inOut', yoyo: true, repeat: -1 });
                gsap.to(chips,   { y: -2, duration: 2.0, ease: 'sine.inOut', yoyo: true, repeat: -1, stagger: { each: 0.35 } });
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="ri-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                <linearGradient id="ri-area" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#7c3aed" stopOpacity="0.22"/><stop offset="1" stopColor="#7c3aed" stopOpacity="0"/>
                </linearGradient>
                <filter id="ri-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#4c1d95" floodOpacity="0.10"/>
                </filter>
                <clipPath id="ri-card-clip">
                    <rect x="18" y="16" width="424" height="250" rx="18"/>
                </clipPath>
            </defs>

            <rect width="460" height="280" fill="url(#ri-bg)"/>
            <rect x="18" y="16" width="424" height="250" rx="18" fill="#fff" filter="url(#ri-shadow)"/>

            <g clipPath="url(#ri-card-clip)">

            {/* Header */}
            <text x="34" y="44" fontSize="13" fontWeight="700" fill="#1a1535" fontFamily="inherit">Earnings Growth</text>
            <g id="ri-badge">
                <rect x="306" y="22" width="120" height="30" rx="15" fill="#7c3aed"/>
                <text x="366" y="42" textAnchor="middle" fontSize="12.5" fill="#fff" fontWeight="800" fontFamily="inherit">30% lifetime</text>
            </g>

            <line x1="34" y1="62" x2="426" y2="62" stroke="#f0ecfc" strokeWidth="1"/>

            {/* Grid */}
            <line x1="64" y1="100" x2="426" y2="100" stroke="#f0ecfc" strokeWidth="1" strokeDasharray="4 4"/>
            <line x1="64" y1="130" x2="426" y2="130" stroke="#f0ecfc" strokeWidth="1" strokeDasharray="4 4"/>
            <line x1="64" y1="160" x2="426" y2="160" stroke="#f0ecfc" strokeWidth="1" strokeDasharray="4 4"/>
            <line x1="64" y1="192" x2="426" y2="192" stroke="#e8e2f8" strokeWidth="1"/>
            <text x="60" y="103" textAnchor="end" fontSize="9" fill="#c4b5fd" fontFamily="inherit">$4k</text>
            <text x="60" y="133" textAnchor="end" fontSize="9" fill="#c4b5fd" fontFamily="inherit">$3k</text>
            <text x="60" y="163" textAnchor="end" fontSize="9" fill="#c4b5fd" fontFamily="inherit">$2k</text>
            <text x="60" y="195" textAnchor="end" fontSize="9" fill="#c4b5fd" fontFamily="inherit">$1k</text>

            {/* Area fill */}
            <path id="ri-area-path"
                d="M68,192 L68,184 C110,172 150,166 198,150 C248,132 292,108 338,84 C368,68 406,58 426,50 L426,192 Z"
                fill="url(#ri-area)"/>

            {/* Chart line */}
            <path id="ri-line" d={PATH_D}
                stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>


            {/* Mid-point dots */}
            <circle id="ri-dot1" cx="198" cy="150" r="4.5" fill="#fff" stroke="#7c3aed" strokeWidth="2"/>
            <circle id="ri-dot2" cx="338" cy="84"  r="4.5" fill="#fff" stroke="#7c3aed" strokeWidth="2"/>

            {/* Peak marker with two pulse rings */}
            <g id="ri-peak">
                <circle id="ri-pulse1" cx="426" cy="50" r="6" fill="#7c3aed" opacity="0"/>
                <circle id="ri-pulse2" cx="426" cy="50" r="6" fill="#7c3aed" opacity="0"/>
                <circle cx="426" cy="50" r="10" fill="#7c3aed" opacity="0.12"/>
                <circle cx="426" cy="50" r="5.5" fill="#7c3aed"/>
                <circle cx="426" cy="50" r="2.5" fill="#fff"/>
            </g>

            {/* Traveling dot — concentric circles, no filter (filters produce square bounding boxes) */}
            <g id="ri-travel-dot" opacity="0">
                <circle r="11" fill="#7c3aed" opacity="0.15"/>
                <circle r="6"  fill="#7c3aed" opacity="0.35"/>
                <circle r="3.5" fill="#fff"/>
            </g>

            {/* Tooltip */}
            <g id="ri-tooltip">
                <line x1="390" y1="68" x2="424" y2="52" stroke="#1a1535" strokeWidth="1" strokeDasharray="2 2" opacity="0.35"/>
                <rect x="348" y="70" width="80" height="24" rx="12" fill="#1a1535"/>
                <text x="388" y="86" textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="700" fontFamily="inherit">$3,420</text>
            </g>

            {/* X-axis labels */}
            <text x="68"  y="210" textAnchor="middle" fontSize="9.5" fill="#b0a0d0" fontFamily="inherit">Jan</text>
            <text x="140" y="210" textAnchor="middle" fontSize="9.5" fill="#b0a0d0" fontFamily="inherit">Feb</text>
            <text x="212" y="210" textAnchor="middle" fontSize="9.5" fill="#b0a0d0" fontFamily="inherit">Mar</text>
            <text x="284" y="210" textAnchor="middle" fontSize="9.5" fill="#b0a0d0" fontFamily="inherit">Apr</text>
            <text x="356" y="210" textAnchor="middle" fontSize="9.5" fill="#b0a0d0" fontFamily="inherit">May</text>
            <text x="420" y="210" textAnchor="middle" fontSize="9.5" fill="#8b6dcc" fontWeight="600" fontFamily="inherit">Jun</text>

            {/* Bottom stat chips */}
            <g className="ri-chip">
                <rect x="34"  y="224" width="118" height="26" rx="13" fill="#f5f0ff"/>
                <text x="93"  y="241" textAnchor="middle" fontSize="9.5" fill="#7c3aed" fontWeight="700" fontFamily="inherit">+22% this month</text>
            </g>
            <g className="ri-chip">
                <rect x="162" y="224" width="116" height="26" rx="13" fill="#dcfce7"/>
                <text x="220" y="241" textAnchor="middle" fontSize="9.5" fill="#16a34a" fontWeight="700" fontFamily="inherit">↑ 8 new referrals</text>
            </g>
            <g className="ri-chip">
                <rect x="288" y="224" width="144" height="26" rx="13" fill="#fff7ed"/>
                <text x="360" y="241" textAnchor="middle" fontSize="9.5" fill="#d97706" fontWeight="700" fontFamily="inherit">Payout Jun 1 · $3,420</text>
            </g>

            </g>{/* end card clip */}
        </svg>
    );
}
